import { z } from 'zod'
import { HtmlString, IsoDateString } from '../../common'
import { guidSchema, localDateSchema, MaintenancePlanPaymentInterval } from '../../contracts'
import { bzOptional } from '..'
import { AccountGuid } from '../Accounts/Account'
import { AccountType } from '../Accounts/AccountType'
import { Address } from '../Address/Address'
import { ContactGuid } from '../contacts/Contact'
import { InvoiceGuid } from '../Finance/Invoicing/InvoiceTypes'
import { PaymentMethod, PaymentRecordGuid } from '../Finance/Payments/PaymentTypes'
import { JobClass, JobGuid } from '../Job'
import { LocationGuid } from '../Locations/Location'
export const getSage100CustomerReportRequestSchema = z.object({
  startDate: localDateSchema,
  endDate: localDateSchema,
})

export const getSage100InvoicesReportRequestSchema = z.object({
  jobLifecycleGuid: guidSchema,
  currentJobLifecycleStatusGuid: guidSchema,
  transitionToJobLifecycleStatusGuid: bzOptional(guidSchema),
})

export const getSage100PaymentsReportRequestSchema = z.object({
  startDate: localDateSchema,
  endDate: localDateSchema,
})

export const getSage100MaintenancePlanInvoicesReportRequestSchema = z.object({
  startDate: localDateSchema,
  endDate: localDateSchema,
})

export type Sage100CustomerReportRequest = z.infer<typeof getSage100CustomerReportRequestSchema>
export type Sage100InvoicesReportRequest = z.infer<typeof getSage100InvoicesReportRequestSchema>
export type Sage100PaymentsReportRequest = z.infer<typeof getSage100PaymentsReportRequestSchema>
export type Sage100MaintenancePlanInvoicesReportRequest = z.infer<
  typeof getSage100MaintenancePlanInvoicesReportRequestSchema
>

export type Sage100Customer = {
  accountGuid: LocationGuid
  createdAt: IsoDateString
  accountReferenceNumber: string
  accountDisplayName: string
  accountType: AccountType
  locations: Array<{
    isBillingAddress: boolean
    locationGuid: LocationGuid
    jobCount: number
    address: Address
  }>
  primaryContact: {
    contactGuid: ContactGuid
    firstName: string
    lastName: string
    primaryEmailAddress?: string
    additionalEmailAddress?: string
    primaryPhoneNumber?: string
    additionalPhoneNumber?: string
  }
}
export type Sage100CustomerReportResponse = {
  customers: Sage100Customer[]
}

export type Sage100Invoice = {
  invoiceGuid: InvoiceGuid
  accountGuid: AccountGuid
  accountReferenceNumber: string
  displayIdV2: string
  dueAt: IsoDateString
  issuedAt: IsoDateString
  subtotalUsc: number
  totalUsc: number
  invoicePayments: Array<{
    appliedAmountUsc: number
    paymentRecord: {
      paymentMethod: PaymentMethod
      paymentMethodAdditionalInfo?: string
    }
  }>
  messageHtml?: HtmlString
  invoiceCartItems: Array<{
    seq: number
    cartItem: {
      description: HtmlString
      quantity: number
      unitPriceUsc: number
    }
  }>
  jobInvoices: Array<{
    jobGuid: JobGuid
    job: {
      displayId: number
      createdAt: IsoDateString
      summary?: string
      jobType: {
        jobClass: JobClass
        name: string
      }
      appointments: Array<{
        appointmentWindowStart: IsoDateString
      }>
      pointOfContact: {
        firstName: string
        lastName: string
        primaryPhoneNumber?: {
          phoneNumber: string
        }
        additionalPhoneNumber?: {
          phoneNumber: string
        }
      }
      location: {
        address: Address
      }
    }
  }>
}

export type Sage100InvoicesReportResponse = {
  invoices: Sage100Invoice[]
}

export type Sage100MaintenancePlanInvoice = {
  invoiceGuid: InvoiceGuid
  accountGuid: AccountGuid
  accountReferenceNumber: string
  displayIdV2: string
  dueAt: IsoDateString
  issuedAt: IsoDateString
  subtotalUsc: number
  totalUsc: number
  invoicePayments: Array<{
    appliedAmountUsc: number
    paymentRecord: {
      paymentMethod: PaymentMethod
      paymentMethodAdditionalInfo?: string
    }
  }>
  messageHtml?: HtmlString
  invoiceCartItems: Array<{
    seq: number
    cartItem: {
      description: HtmlString
      quantity: number
      unitPriceUsc: number
    }
  }>
  primaryContact: {
    firstName: string
    lastName: string
    primaryPhoneNumber?: string
    additionalPhoneNumber?: string
  }
  maintenancePlan: {
    name: string
    paymentInterval?: MaintenancePlanPaymentInterval
    location: {
      address: Address
    }
  }
}

export type Sage100MaintenancePlanInvoicesReportResponse = {
  invoices: Sage100MaintenancePlanInvoice[]
}

export type Sage100Payment = {
  paymentRecordGuid: PaymentRecordGuid
  occurredAt: IsoDateString
  paymentMethod: PaymentMethod
  paymentMethodAdditionalInfo?: string
  amountUsd: number
  externalPaymentId?: string
  accountReferenceNumber: string
  invoicePayments: Array<{
    invoiceGuid: InvoiceGuid
    appliedAmountUsc: number
    invoice: {
      displayIdV2: string
    }
  }>
}
export type Sage100PaymentsReportResponse = {
  payments: Sage100Payment[]
}
